<template>
  <b-modal
    :visible="value"
    @input="$emit('input', $event)"
    title="Service Subscription"
    size="lg"
    @ok="handleSave"
    @cancel="handleCancel"
    ok-title="Subscribe"
    cancel-title="Cancel"
  >
    <b-form>
      <!-- Device ID Input -->
      <b-form-group
        label="Device ID:"
        label-for="device-input"
        class="mb-3"
      >
        <b-form-input
          id="device-input"
          v-model="formData.deviceId"
          type="text"
          :disabled="isEdit"
          placeholder="Enter device ID"
          required
        />
      </b-form-group>

      <!-- Service Type Selection -->
      <b-form-group
        label="Service Type:"
        label-for="service-select"
        class="mb-3"
      >
        <b-form-select
          id="service-select"
          v-model="formData.serviceType"
          :options="serviceTypeOptions"
          :disabled="isEdit"
          required
        />
      </b-form-group>

      <!-- Current Status (if editing) -->
      <b-form-group v-if="isEdit" label="Current Status:" class="mb-3">
        <b-badge :variant="getStatusBadge(serviceData.status)">
          {{ getStatusInfo(serviceData.status).icon }}
          {{ serviceData.status }}
        </b-badge>
      </b-form-group>

      <!-- Start Date (read-only) -->
      <b-form-group
        label="Start Date:"
        label-for="start-date"
        class="mb-3"
      >
        <b-form-input
          id="start-date"
          :value="formData.startDate"
          type="date"
          disabled
        />
      </b-form-group>

      <!-- End Date -->
      <b-form-group class="mb-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <label for="end-date">End Date:</label>
          <b-form-checkbox
            v-model="formData.unlimited"
            switch
          >
            Unlimited
          </b-form-checkbox>
        </div>
        <b-form-input
          id="end-date"
          v-model="formData.endDate"
          type="date"
          :disabled="formData.unlimited"
        />
      </b-form-group>

      <!-- Notes -->
      <b-form-group
        label="Notes (optional):"
        label-for="notes"
        class="mb-3"
      >
        <b-form-textarea
          id="notes"
          v-model="formData.notes"
          rows="3"
          placeholder="Enter notes or comments..."
        />
      </b-form-group>
    </b-form>
  </b-modal>
</template>

<script>
import { subscribeToService } from '@/helpers/apiServices'
import { getStatusInfo } from '@/helpers/apiServices'
import { useServicesStore } from '@/stores/services'

export default {
  name: 'ServiceModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    serviceData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      formData: {
        deviceId: '',
        serviceType: '',
        startDate: this.getTodayDate(),
        endDate: this.getNextYearDate(),
        unlimited: false,
        notes: ''
      },
      serviceTypeOptions: [
        { value: 'AIS', text: 'AIS Service' },
        { value: 'Navarea', text: 'Navarea' },
        { value: 'Weather', text: 'Weather' }
      ],
      isLoading: false
    }
  },
  computed: {
    isEdit() {
      return this.serviceData?.isEdit || false
    }
  },
  watch: {
    serviceData(newVal) {
      if (newVal) {
        this.formData = {
          deviceId: newVal.device_id,
          serviceType: newVal.service_type,
          startDate: newVal.start_date ? newVal.start_date.split('T')[0] : this.getTodayDate(),
          endDate: newVal.end_date_raw ? newVal.end_date_raw.split('T')[0] : this.getNextYearDate(),
          unlimited: !newVal.end_date_raw,
          notes: newVal.notes || ''
        }
      }
    }
  },
  methods: {
    getTodayDate() {
      const today = new Date()
      return today.toISOString().split('T')[0]
    },
    getNextYearDate() {
      const next = new Date()
      next.setFullYear(next.getFullYear() + 1)
      return next.toISOString().split('T')[0]
    },
    getStatusInfo(status) {
      return getStatusInfo(status)
    },
    getStatusBadge(status) {
      return this.getStatusInfo(status).badge
    },
    async handleSave() {
      if (!this.formData.deviceId || !this.formData.serviceType) {
        alert('Please fill in all required fields')
        return
      }

      this.isLoading = true

      try {
        const endDate = this.formData.unlimited ? null : this.formData.endDate + 'T00:00:00'

        await subscribeToService(
          this.formData.deviceId,
          this.formData.serviceType,
          endDate,
          this.formData.notes || null
        )

        const servicesStore = useServicesStore()
        servicesStore.clearError()

        this.$bvToast.toast('Service subscription saved successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        })

        this.$emit('save', this.formData)
        this.$emit('input', false)
      } catch (error) {
        const errorMsg = error.detail || error.message || 'Failed to save subscription'
        const servicesStore = useServicesStore()
        servicesStore.setError(errorMsg)

        this.$bvToast.toast(errorMsg, {
          title: 'Error',
          variant: 'danger',
          solid: true
        })
      } finally {
        this.isLoading = false
      }
    },
    handleCancel() {
      this.$emit('input', false)
      this.resetForm()
    },
    resetForm() {
      this.formData = {
        deviceId: '',
        serviceType: '',
        startDate: this.getTodayDate(),
        endDate: this.getNextYearDate(),
        unlimited: false,
        notes: ''
      }
    }
  }
}
</script>

<style scoped>
.form-group label {
  font-weight: 600;
  color: #333;
}

.form-control:disabled {
  background-color: #e9ecef;
}

.b-form-checkbox {
  margin-top: 0.5rem;
}
</style>
